import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import './Dialog.css';

export const Dialog = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info', // 'success', 'error', 'warning', 'confirm'
  onConfirm,
  confirmText,
  cancelText,
  showCancel = false
}) => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    console.log('[Dialog] Confirm button clicked');
    console.log('[Dialog] onConfirm type:', typeof onConfirm, onConfirm);
    if (typeof onConfirm === 'function') {
      console.log('[Dialog] Executing onConfirm callback');
      await onConfirm();
      console.log('[Dialog] onConfirm callback completed');
    } else {
      console.warn('[Dialog] onConfirm is not a function!');
    }
    console.log('[Dialog] Closing dialog');
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <div className="dialog-icon success">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#4CAF50" strokeWidth="2" fill="none"/>
              <path d="M14 24l8 8 12-16" stroke="#4CAF50" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="dialog-icon error">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#f44336" strokeWidth="2" fill="none"/>
              <path d="M16 16l16 16M32 16l-16 16" stroke="#f44336" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="dialog-icon warning">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#ff9800" strokeWidth="2" fill="none"/>
              <path d="M24 14v12M24 30v2" stroke="#ff9800" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        );
      case 'confirm':
        return (
          <div className="dialog-icon confirm">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#2196F3" strokeWidth="2" fill="none"/>
              <path d="M24 18v8M24 30h.01" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        );
      default:
        return (
          <div className="dialog-icon info">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#2196F3" strokeWidth="2" fill="none"/>
              <path d="M24 22v10M24 14v2" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container">
        <div className="dialog-content">
          {getIcon()}
          {title && <h3 className="dialog-title">{title}</h3>}
          <p className="dialog-message">{message}</p>
        </div>
        <div className="dialog-actions">
          {showCancel && (
            <button className="dialog-btn dialog-btn-cancel" onClick={onClose}>
              {cancelText || t('cancel')}
            </button>
          )}
          <button
            className={`dialog-btn dialog-btn-confirm ${type}`}
            onClick={handleConfirm}
          >
            {confirmText || t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook để sử dụng Dialog dễ dàng hơn
export const useDialog = () => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [dialogState, setDialogState] = React.useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    showCancel: false,
    confirmText: '',
    cancelText: ''
  });

  const showDialog = (options) => {
    setDialogState({
      isOpen: true,
      ...options
    });
  };

  const closeDialog = () => {
    setDialogState(prev => ({ ...prev, isOpen: false }));
  };

  const showSuccess = (message, title) => {
    showDialog({ message, title: title || t('success'), type: 'success' });
  };

  const showError = (message, title) => {
    showDialog({ message, title: title || t('error'), type: 'error' });
  };

  const showWarning = (message, title) => {
    showDialog({ message, title: title || t('error'), type: 'warning' });
  };

  const showConfirm = (message, onConfirm, title) => {
    console.log('[useDialog] showConfirm called with:', {
      message,
      onConfirm: typeof onConfirm,
      onConfirmValue: onConfirm,
      title
    });
    showDialog({
      message,
      title: title || t('confirm'),
      type: 'confirm',
      onConfirm,
      showCancel: true
    });
  };

  const DialogComponent = () => (
    <Dialog
      {...dialogState}
      onClose={closeDialog}
    />
  );

  return {
    showDialog,
    showSuccess,
    showError,
    showWarning,
    showConfirm,
    closeDialog,
    DialogComponent
  };
};
